/**
 * Database Connections API endpoints for managing saved database credentials.
 */

import { api } from './client';
import type { ApiResponse } from '../types';

/** Database connection data */
export interface DatabaseConnection {
  id: number;
  name: string;
  /** Legacy SQLite records may be returned for inspection/removal, not new server profiles. */
  db_type: 'postgres' | 'mysql' | 'sqlite';
  connection_preview: string;
  ssh_enabled: boolean;
  ssh_host?: string;
  ssh_port?: number;
  ssh_username?: string;
  created_at: string;
  updated_at: string;
}

/** Request body for creating a database connection */
export interface CreateDatabaseConnectionRequest {
  name: string;
  db_type: 'postgres' | 'mysql';
  /** Direct connection string (used if Supabase fields not provided) */
  connection_string?: string;
  /** For Supabase: project reference */
  supabase_project_ref?: string;
  /** For Supabase: database password */
  supabase_password?: string;
  /** For Supabase: region (e.g., "us-east-1") */
  supabase_region?: string;
  /** For Supabase: use session pooler (IPv4) instead of direct (IPv6) */
  supabase_use_pooler?: boolean;
  /** Enable SSH tunnel for this connection */
  ssh_enabled?: boolean;
  /** SSH host */
  ssh_host?: string;
  /** SSH port (default: 22) */
  ssh_port?: number;
  /** SSH username */
  ssh_username?: string;
  /** SSH password */
  ssh_password?: string;
  /** Database name to create/connect to (non-Supabase Postgres/MySQL) */
  database_name?: string;
  /** Create the database if it doesn't exist (default true when database_name provided) */
  create_db_if_not_exists?: boolean;
  /** Drop and recreate the database before connecting (destructive) */
  clear_existing_db?: boolean;
  /** Required exact database name for an explicitly reviewed destructive replacement. */
  confirm_clear_database_name?: string;
}

/** Request body for updating a database connection (all fields optional) */
export interface UpdateDatabaseConnectionRequest {
  name?: string;
  db_type?: 'postgres' | 'mysql' | 'sqlite';
  connection_string?: string;
  supabase_project_ref?: string;
  supabase_password?: string;
  supabase_region?: string;
  supabase_use_pooler?: boolean;
  ssh_enabled?: boolean;
  ssh_host?: string;
  ssh_port?: number;
  ssh_username?: string;
  ssh_password?: string;
}

/** Response for test connection endpoint */
export interface TestConnectionResponse {
  success: boolean;
  healthy: boolean;
  message: string;
}

/** Database Connections API (site-specific) */
export const databaseConnectionsApi = {
  /** List all database connections for a site */
  list: (siteSlug: string) =>
    api.get<ApiResponse<DatabaseConnection[]>>(
      `/v1/sites/${siteSlug}/database-connections`
    ),

  /** Get a specific database connection */
  get: (siteSlug: string, id: number) =>
    api.get<ApiResponse<DatabaseConnection>>(
      `/v1/sites/${siteSlug}/database-connections/${id}`
    ),

  /** Create a new database connection */
  create: (siteSlug: string, data: CreateDatabaseConnectionRequest) =>
    api.post<ApiResponse<DatabaseConnection>>(
      `/v1/sites/${siteSlug}/database-connections`,
      data
    ),

  /** Update a database connection */
  update: (siteSlug: string, id: number, data: UpdateDatabaseConnectionRequest) =>
    api.put<ApiResponse<DatabaseConnection>>(
      `/v1/sites/${siteSlug}/database-connections/${id}`,
      data
    ),

  /** Delete a database connection */
  delete: (siteSlug: string, id: number) =>
    api.delete<ApiResponse<null>>(
      `/v1/sites/${siteSlug}/database-connections/${id}`
    ),

  /** Test a database connection */
  test: (siteSlug: string, id: number) =>
    api.post<ApiResponse<TestConnectionResponse>>(
      `/v1/sites/${siteSlug}/database-connections/${id}/test`,
      {}
    ),
};

/** Account-level Database Connections API (no site context) */
export const accountDatabasesApi = {
  /** List all account-level database connections */
  list: () => api.get<ApiResponse<DatabaseConnection[]>>('/v1/databases'),

  /** Get a specific account-level database connection */
  get: (id: number) => api.get<ApiResponse<DatabaseConnection>>(`/v1/databases/${id}`),

  /** Create a new account-level database connection */
  create: (data: CreateDatabaseConnectionRequest) =>
    api.post<ApiResponse<DatabaseConnection>>('/v1/databases', data),

  /** Update an account-level database connection */
  update: (id: number, data: UpdateDatabaseConnectionRequest) =>
    api.put<ApiResponse<DatabaseConnection>>(`/v1/databases/${id}`, data),

  /** Delete an account-level database connection */
  delete: (id: number) => api.delete<ApiResponse<null>>(`/v1/databases/${id}`),

  /** Test an account-level database connection by ID */
  test: (id: number) =>
    api.post<ApiResponse<TestConnectionResponse>>(`/v1/databases/${id}/test`, {}),

  /** Test connection details without saving (inline test) */
  testInline: (data: CreateDatabaseConnectionRequest) =>
    api.post<ApiResponse<TestConnectionResponse>>('/v1/databases/test-inline', data),
};
